'use client'

/**
 * EmbedThisCard — the "Embed this" affordance on each widget's source page
 * (FRIDGE-356): /data/price-index, /data/space-efficiency, /data/energy-index,
 * /tools/refrigerator-size-calculator and /tools/energy-cost-calculator.
 *
 * One light card, house palette, that hands a publisher two things:
 *   1. the copy-ready `<iframe>` snippet (plus its caption line, which puts the
 *      attribution link in the HOST page's own HTML — that caption is the
 *      backlink), with an on-demand live preview of the exact widget;
 *   2. a script-free fallback — a static line + link — for surfaces that strip
 *      iframes.
 *
 * Both snippets come from lib/embed/assets.ts, the same registry the /embed
 * route renders from, so the card can never advertise a widget that does not
 * exist. Client-side only for the copy buttons (reused from the citation kit)
 * and the preview toggle; the preview iframe mounts only when asked, so a
 * page view costs no extra request.
 */
import { useState } from 'react'
import { Code2, Eye, EyeOff } from 'lucide-react'
import { CopyBtn } from '@/components/data/citation-kit'
import {
  EMBED_ASSETS,
  embedAttributionLabel,
  embedFallbackSnippet,
  embedIframeSnippet,
  type EmbedAssetSlug,
} from '@/lib/embed/assets'

export function EmbedThisCard({ asset }: { asset: EmbedAssetSlug }) {
  const a = EMBED_ASSETS[asset]
  const iframeSnippet = embedIframeSnippet(asset)
  const fallbackSnippet = embedFallbackSnippet(asset)
  const [preview, setPreview] = useState(false)
  const headingId = `embed-this-${asset}`

  return (
    <section aria-labelledby={headingId} data-embed-card={asset} className="mt-8">
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-6 md:px-8 py-5 border-b border-gray-200 bg-[#0469f0]/[0.03] flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0469f0]/10 flex items-center justify-center shrink-0">
            <Code2 className="w-5 h-5 text-[#0469f0]" />
          </div>
          <div className="min-w-0">
            <h2 id={headingId} className="text-lg font-bold text-gray-900">
              Embed this {a.kind === 'data' ? 'index' : 'calculator'} on your site
            </h2>
            <p className="text-[13px] text-gray-600">
              A live Fridge.com widget — {a.description} Free to embed; keep the attribution line
              visible.
            </p>
          </div>
        </div>

        <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Iframe snippet + on-demand preview */}
          <div className="min-w-0">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">
                Copy-paste embed code
              </h3>
              <CopyBtn text={iframeSnippet} label="embed code" />
            </div>
            <pre className="text-[11px] leading-relaxed text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto m-0 whitespace-pre-wrap break-words">
              {iframeSnippet}
            </pre>
            <p className="mt-2 text-[12px] text-gray-600 leading-relaxed">
              The widget sizes itself to the iframe&rsquo;s width — any column from about 300px up.
              Keep the height at {a.height}px or more. The caption under the iframe is the
              attribution: <span className="font-semibold text-gray-900">{embedAttributionLabel(asset)}</span>.
            </p>
            <button
              type="button"
              onClick={() => setPreview((v) => !v)}
              aria-expanded={preview}
              className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-gray-200 bg-white text-[12px] font-bold text-gray-600 hover:border-[#ff9951] hover:text-gray-900 transition-colors"
            >
              {preview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {preview ? 'Hide preview' : 'Show live preview'}
            </button>
            {preview && (
              <div className="mt-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3">
                <iframe
                  src={`/embed/${asset}`}
                  title={`${a.name} — Fridge.com widget preview`}
                  width="100%"
                  height={a.height}
                  style={{ border: 0, maxWidth: a.maxWidth, display: 'block' }}
                  loading="lazy"
                />
              </div>
            )}
          </div>

          {/* Script-free fallback */}
          <div className="min-w-0">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">
                No-iframe fallback (static line + link)
              </h3>
              <CopyBtn text={fallbackSnippet} label="fallback code" />
            </div>
            <pre className="text-[11px] leading-relaxed text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto m-0 whitespace-pre-wrap break-words">
              {fallbackSnippet}
            </pre>
            <p className="mt-2 text-[12px] text-gray-600 leading-relaxed">
              For newsletters, AMP pages and CMS blocks that strip iframes: the same attribution
              as a plain line and link. Nothing to load, nothing to update.
            </p>
            <div className="mt-4 rounded-lg bg-[#ff9951]/[0.08] border border-[#ff9951]/30 p-3.5">
              <p className="text-[13px] text-gray-700 leading-relaxed">
                <strong className="text-gray-900">Terms:</strong> the widget is free to embed on any
                site under CC BY 4.0 — keep the attribution line and its link visible and unmodified.{' '}
                {a.kind === 'data'
                  ? 'Every figure it shows is the same self-computed number as this page.'
                  : 'It runs the same calculation as this page.'}{' '}
                It carries no retailer links, and it records nothing a visitor types.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
